import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { client } from '../../client';
import { type CustomBenchmark, type CustomBenchmarkResponse, toCustomBenchmark } from '../../queries/get-benchmark';

/*
 * Hand-written stand-in for PATCH /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}, live behind the API's
 * `custom-benchmarks` flag but missing from the spec the generator reads. Omitted fields stay as they are; the API
 * refuses an explicit `null` name. Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

const UPDATE_BENCHMARK_URL = '/v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}';

export interface UpdateBenchmarkRequest {
  name?: string;
  description?: string | null;
}

export type UpdateBenchmarkMutationVariables = { workspace_id: string; benchmark_id: string } & UpdateBenchmarkRequest;

export type UpdateBenchmarkMutation = CustomBenchmark;

async function updateBenchmark({ workspace_id, benchmark_id, ...body }: UpdateBenchmarkMutationVariables) {
  const { data } = await client.patch<{ 200: CustomBenchmarkResponse }, unknown, true>({
    url: UPDATE_BENCHMARK_URL,
    path: { workspace_id, benchmark_id },
    body,
    throwOnError: true,
  });
  return toCustomBenchmark(data);
}

export const useUpdateBenchmarkMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<UpdateBenchmarkMutation, TError, UpdateBenchmarkMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<UpdateBenchmarkMutation, TError, UpdateBenchmarkMutationVariables, TContext>({
    mutationKey: ['UpdateBenchmark'],
    mutationFn: updateBenchmark,
    ...options,
  });
};

useUpdateBenchmarkMutation.fetcher = (variables: UpdateBenchmarkMutationVariables) => () => updateBenchmark(variables);
